(function () {
  'use strict';
  if (window.TACFinalization) return;

  const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));
  const localeRoot = () => String(window.Shopify?.routes?.root || '/').replace(/\/+$/, '');
  const route = path => (localeRoot() || '') + path;
  const bundleId = item => String(item?.properties?._tac_bundle_id || '');
  const DISPLAY_NAMES_PROP = '_tacDisplayNames';
  let finalizationWork = null;
  const signature = item => {
    const props=item.properties || {};
    return [Number(item.variant_id || item.id),props._tac_contract_version||'',props._tac_bundle_id||'',props._tac_manifest_id||'',props._tac_manifest_hash||'',props._tac_bundle_role||'',props._tac_bundle_type||''].join('|');
  };

  function normalizeDisplayNames(value) {
    let text=String(value || '');
    if (typeof text.normalize==='function') text=text.normalize('NFC');
    text=text.replace(/[\u0000-\u001f\u007f<>]/g,' ').replace(/\s+/gu,' ').trim();
    return Array.from(text).slice(0,80).join('');
  }

  function currentDisplayNames() {
    return normalizeDisplayNames(window.tacCustomizer?.data?.portada?.nombres);
  }

  function withCartPresentation(expected) {
    const displayNames=currentDisplayNames();
    return (expected || []).map(item=>{
      const properties={...(item?.properties || {})};
      if (properties._tac_bundle_role==='parent' && displayNames) properties[DISPLAY_NAMES_PROP]=displayNames;
      else delete properties[DISPLAY_NAMES_PROP];
      return {...item,properties};
    });
  }

  function isComplete(cart, expected, id) {
    const actual=(cart?.items || []).filter(item=>bundleId(item)===id);
    if (actual.length!==expected.length) return false;
    return expected.every(item=>{
      const match=actual.find(line=>signature(line)===signature({variant_id:item.id,properties:item.properties}));
      if (!match || Number(match.quantity||0)!==Number(item.quantity||1)) return false;
      return normalizeDisplayNames(match.properties?.[DISPLAY_NAMES_PROP])===normalizeDisplayNames(item.properties?.[DISPLAY_NAMES_PROP]);
    });
  }

  async function jsonRequest(path, options) {
    const response=await fetch(route(path),options);
    let data={}; try { data=await response.json(); } catch {}
    if (!response.ok) { const error=new Error(data?.error || `cart_${response.status}`); error.status=response.status; throw error; }
    return data;
  }

  async function reconcileUnlocked(id, expected) {
    let cart=await jsonRequest('/cart.js',{headers:{Accept:'application/json'},cache:'no-store'});
    if (isComplete(cart,expected,id)) return {status:'complete',cart};
    const updates={};
    (cart.items||[]).filter(item=>bundleId(item)===id).forEach(item=>{ updates[item.key]=0; });
    if (Object.keys(updates).length) await jsonRequest('/cart/update.js',{method:'POST',headers:{'Content-Type':'application/json',Accept:'application/json'},body:JSON.stringify({updates})});
    window.TACTrace?.('CART_ADD_START', { bundle: true });
    await jsonRequest('/cart/add.js',{method:'POST',headers:{'Content-Type':'application/json',Accept:'application/json'},body:JSON.stringify({items:expected})});
    cart=await jsonRequest('/cart.js',{headers:{Accept:'application/json'},cache:'no-store'});
    if (!isComplete(cart,expected,id)) throw new Error('bundle_cart_reconciliation_failed');
    return {status:'reconciled',cart};
  }

  async function reconcileCart(id, expected) {
    if (!id || !Array.isArray(expected) || !expected.length) throw new Error('bundle_cart_contract_invalid');
    const presented=withCartPresentation(expected);
    const work=()=>reconcileUnlocked(id,presented);
    return navigator.locks?.request ? navigator.locks.request(`tac-cart-${id}`,work) : work();
  }

  async function apiBase() {
    const config=typeof tacDraftGetConfig==='function' ? await tacDraftGetConfig() : window.tacDraftClient?.config;
    const base=String(config?.api_base_url || '').replace(/\/+$/,'');
    if (!base) throw new Error('draft_api_unavailable');
    let parsed;
    try { parsed=new URL(base); } catch { throw new Error('draft_api_invalid'); }
    const expected=String(window.tacDraftClient?.apiBaseUrl || '').replace(/\/+$/,'');
    if (parsed.protocol!=='https:' || parsed.origin!==base || (expected && expected!==base)) throw new Error('draft_api_invalid');
    return parsed.origin;
  }

  async function readFinalization(base,session) {
    const response=await fetch(`${base}/v1/drafts/${encodeURIComponent(session.id)}/finalization`,{
      method:'GET',headers:{Authorization:`Bearer ${session.token}`,Accept:'application/json'},cache:'no-store'
    });
    let result={};try{result=await response.json();}catch{}
    if(response.status===404)return null;
    if(!response.ok)throw new Error(result?.error||`finalization_recovery_${response.status}`);
    return result;
  }

  async function finalizeSession(session) {
    const base=await apiBase();
    window.TACTrace?.('FINALIZE_START');
    let last;
    for (let attempt=0;attempt<8;attempt++) {
      let response;
      try {
        response=await fetch(`${base}/v1/drafts/${encodeURIComponent(session.id)}/finalize`,{method:'POST',headers:{Authorization:`Bearer ${session.token}`,'Content-Type':'application/json',Accept:'application/json'},body:JSON.stringify({expected_revision:Number(session.revision)})});
      } catch (error) {
        const recovered=await readFinalization(base,session).catch(()=>null);
        last=recovered||{error:'finalization_network_error'};
        await sleep(350*(attempt+1));
        continue;
      }
      let result={}; try { result=await response.json(); } catch {}
      last=result;
      if (response.ok && result?.finalization?.phase==='ready') {
        window.TACTrace?.('FINALIZE_READY');
        return result;
      }
      if (![202,503].includes(response.status) || !['bundle_freeze_incomplete',undefined].includes(result?.error)) throw new Error(result?.error || `finalize_${response.status}`);
      await sleep(350*(attempt+1));
    }
    throw new Error(last?.error || 'finalization_timeout');
  }

  async function finalizeAndAddToCartUnlocked({goToCart=true}={}) {
    const client=window.tacDraftClient;
    if (client?.saveState==='conflict') throw new Error('draft_not_ready_to_finalize');
    if (!client?.hydrationReady && client?.finalizationResult?.finalization?.phase==='ready') {
      const cached=client.finalizationResult;
      await reconcileCart(cached.finalization.bundle_id,cached.cart_items);
      if (goToCart) { window.TACTrace?.('CART_REDIRECT'); window.location.assign(route('/cart')); }
      return cached;
    }
    if (!client?.hydrationReady) throw new Error('draft_not_ready_to_finalize');
    clearTimeout(client.saveTimer);
    const saved=typeof tacDraftFlushSave==='function' ? await tacDraftFlushSave('finalize') : false;
    if (!saved && client.saveState!=='saved') throw new Error('draft_save_before_finalize_failed');
    const session=await tacEnsureDraftSession();
    const result=await finalizeSession(session);
    client.hydrationReady=false;
    client.finalization=result.finalization;
    client.finalizationResult=result;
    session.finalization=result.finalization;
    await reconcileCart(result.finalization.bundle_id,result.cart_items);
    if (goToCart) {
      window.TACTrace?.('CART_REDIRECT');
      window.location.assign(route('/cart'));
    }
    return result;
  }

  function finalizeAndAddToCart(options={}) {
    if (finalizationWork) return finalizationWork;
    finalizationWork=finalizeAndAddToCartUnlocked(options).finally(()=>{ finalizationWork=null; });
    return finalizationWork;
  }

  async function cartStateFor(result) {
    const id=String(result?.finalization?.bundle_id || '');
    const expected=withCartPresentation(result?.cart_items);
    if (!id || !Array.isArray(expected) || !expected.length) throw new Error('bundle_cart_contract_invalid');
    const cart=await jsonRequest('/cart.js',{headers:{Accept:'application/json'},cache:'no-store'});
    return {id,expected,cart,present:isComplete(cart,expected,id)};
  }

  function ensureChoiceStyles() {
    if (document.getElementById('tac-finalized-choice-style')) return;
    const style=document.createElement('style');
    style.id='tac-finalized-choice-style';
    style.textContent=`
      .tac-finalized-choice{position:fixed;inset:0;z-index:2147483647;display:grid;place-items:center;padding:20px;background:rgba(17,17,17,.38);box-sizing:border-box}
      .tac-finalized-choice__card{width:min(440px,100%);padding:28px;border-radius:24px;background:#fff;color:#171717;box-shadow:0 24px 70px rgba(0,0,0,.24);text-align:center}
      .tac-finalized-choice__card h2{margin:0 0 10px;font:700 26px/1.15 "Quattrocento Sans",system-ui,sans-serif}
      .tac-finalized-choice__card p{margin:0 0 22px;font:400 16px/1.5 system-ui,sans-serif;color:#555}
      .tac-finalized-choice__actions{display:grid;gap:10px}
      .tac-finalized-choice button{min-height:48px;border-radius:12px;padding:12px 18px;font:700 15px/1.2 system-ui,sans-serif;cursor:pointer}
      .tac-finalized-choice__fresh{border:1px solid #111;background:#111;color:#fff}
      .tac-finalized-choice__recover{border:1px solid #ccc;background:#fff;color:#111}
      .tac-finalized-choice button:focus-visible{outline:3px solid #6b8afd;outline-offset:3px}
    `;
    document.head.appendChild(style);
  }

  function requestFinalizedChoice() {
    ensureChoiceStyles();
    return new Promise(resolve=>{
      const english=String(window.tacLang || '').toLowerCase().startsWith('en');
      const overlay=document.createElement('div');
      overlay.className='tac-finalized-choice';
      overlay.setAttribute('role','dialog');
      overlay.setAttribute('aria-modal','true');
      overlay.setAttribute('aria-labelledby','tac-finalized-choice-title');
      const card=document.createElement('div');
      card.className='tac-finalized-choice__card';
      const title=document.createElement('h2');
      title.id='tac-finalized-choice-title';
      title.textContent=english ? 'We found your previous design ✨' : 'Encontramos tu diseño anterior ✨';
      const copy=document.createElement('p');
      copy.textContent=english ? 'You removed this album from your cart. What would you like to do?' : 'Quitaste este álbum del carrito. ¿Qué quieres hacer?';
      const actions=document.createElement('div');
      actions.className='tac-finalized-choice__actions';
      const fresh=document.createElement('button');
      fresh.type='button'; fresh.className='tac-finalized-choice__fresh';
      fresh.textContent=english ? 'Start over' : 'Empezar de cero';
      const recover=document.createElement('button');
      recover.type='button'; recover.className='tac-finalized-choice__recover';
      recover.textContent=english ? 'Recover my design' : 'Recuperar mi diseño';
      actions.append(fresh,recover); card.append(title,copy,actions); overlay.append(card);
      const finish=choice=>{ document.removeEventListener('keydown',onKey,true); overlay.remove(); resolve(choice); };
      const onKey=event=>{ if(event.key==='Escape'){ event.preventDefault(); finish('cancel'); } };
      fresh.addEventListener('click',()=>finish('start_over'),{once:true});
      recover.addEventListener('click',()=>finish('recover'),{once:true});
      document.addEventListener('keydown',onKey,true);
      (document.getElementById('customizer-hub') || document.body).appendChild(overlay);
      fresh.focus();
    });
  }

  async function resolveStored(session,{goToCart=true,choose=requestFinalizedChoice}={}) {
    const result=await finalizeSession(session);
    window.tacDraftClient.finalization=result.finalization;
    window.tacDraftClient.hydrationReady=false;
    const state=await cartStateFor(result);
    if (state.present) {
      if (goToCart) { window.TACTrace?.('CART_REDIRECT'); window.location.assign(route('/cart')); }
      return {action:'present',result};
    }
    const choice=await choose();
    if (choice==='start_over') return {action:'start_over',result};
    if (choice==='cancel') return {action:'cancel',result};
    await reconcileCart(state.id,state.expected);
    if (goToCart) { window.TACTrace?.('CART_REDIRECT'); window.location.assign(route('/cart')); }
    return {action:'recover',result};
  }

  const resumeStored=resolveStored;
  window.TACFinalization={finalizeAndAddToCart,resumeStored,resolveStored,reconcileCart,_test:{isComplete,signature,apiBase,finalizeSession,readFinalization,cartStateFor,requestFinalizedChoice,normalizeDisplayNames,withCartPresentation}};
})();
